// Munos KV backend contract + the dependency-free in-memory backend.
//
// The db_* builtins are implemented once (see env-bindings.ts) against
// this interface; each tier supplies its own durable backend — SQLite
// on the server, LocalStorage in the browser — while tests and the
// headless dummy default to InMemoryMunosKv. The interface lives here
// (browser-safe, zero deps) so the server and web packages stop
// declaring their own structurally-identical copies.
// The reserved scope every `@`-prefixed namespace routes to — the single
// public tier any script can read and write (see docs/persistence.md
// §"The shared tier"). It can't collide with a real scope: server-side
// scopes are lowercased `.munos` basenames and client-side scopes are
// fetch URLs (which contain `://`); neither is a bare "@". The `@`-routing
// itself lives in one place — makeDbEnv (env-bindings.ts) — so every tier
// agrees. Backends just store whatever scope they're handed.
export const SHARED_SCOPE = "@";
// The canonical ordered-page definition for db_list, shared by every tier so
// the result is byte-identical across backends (the "M17" property the fidelity
// rig relies on). Applied at the single choke point in makeDbEnv to whatever a
// backend returns, and used directly by InMemoryMunosKv. Pure and total.
//
// Order is JS default string sort (UTF-16 code-unit order) — spec-defined and
// identical on every JS engine. munos keys are ASCII in practice, where that
// order coincides with SQLite's BINARY (UTF-8 byte) collation, so the SQL
// backends' pushed-down ORDER BY / range bounds agree with this definition; the
// cross-tier parity test asserts it rather than trusting it silently.
export function pageKeys(keys, prefix, start, limit, reverse) {
    let out = keys.filter(k => k.startsWith(prefix));
    if (reverse) {
        if (start !== "")
            out = out.filter(k => k < start);
        out.sort();
        out.reverse();
    }
    else {
        if (start !== "")
            out = out.filter(k => k > start);
        out.sort();
    }
    if (limit > 0 && out.length > limit)
        out = out.slice(0, limit);
    return out;
}
// Smallest string strictly greater than every string starting with `prefix`
// (its exclusive upper bound), or null when `prefix` is empty or has none.
// Increment the last UTF-16 code unit, carrying past any 0xFFFF. For the
// ASCII/BMP keys munos uses this matches both JS `<` and SQLite BINARY order,
// so the SQL backends can bound a range scan to EXACTLY the prefix matches —
// which is what lets them push down `LIMIT` and return only the page.
export function prefixUpperBound(prefix) {
    for (let i = prefix.length - 1; i >= 0; i--) {
        const c = prefix.charCodeAt(i);
        if (c < 0xffff)
            return prefix.slice(0, i) + String.fromCharCode(c + 1);
        // c === 0xffff: drop it and carry into the previous unit.
    }
    return null;
}
// Build the SQL tail (WHERE bounds + ORDER BY + LIMIT) for a pushed-down
// db_list page, appended after `... WHERE scope = ? AND namespace = ?`. Shared
// by every SQLite tier (libSQL/Turso, SQLite-WASM, OPFS worker) so their range
// queries stay in lockstep with `pageKeys` and with each other. Positional `?`
// placeholders; `args` are appended to the caller's [scope, ns] in order.
//
// The bounds keep the scan inside the prefix range so a pushed-down `LIMIT`
// counts only true prefix matches (a bare `key >= prefix` with LIMIT could fill
// the page with rows past the prefix that the exact startsWith filter then
// drops). Callers still apply an exact startsWith afterward as a safety net, and
// makeDbEnv re-applies `pageKeys` for the canonical guarantee.
export function buildKvListSql(prefix, start, limit, reverse) {
    const clauses = [];
    const args = [];
    const upper = prefixUpperBound(prefix);
    if (!reverse) {
        if (start !== "") {
            clauses.push("key > ?");
            args.push(start);
        }
        else if (prefix !== "") {
            clauses.push("key >= ?");
            args.push(prefix);
        }
        if (upper !== null) {
            clauses.push("key < ?");
            args.push(upper);
        }
    }
    else {
        if (prefix !== "") {
            clauses.push("key >= ?");
            args.push(prefix);
        }
        if (start !== "") {
            clauses.push("key < ?");
            args.push(start);
        }
        else if (upper !== null) {
            clauses.push("key < ?");
            args.push(upper);
        }
    }
    let sql = clauses.length > 0 ? " AND " + clauses.join(" AND ") : "";
    sql += reverse ? " ORDER BY key DESC" : " ORDER BY key ASC";
    if (limit > 0) {
        sql += " LIMIT ?";
        args.push(limit);
    }
    return { sql, args };
}
// Spec-suggested server-side default. Client-side backends typically
// pass a smaller quota (the browser's LocalStorage backing uses 1 MiB).
export const DEFAULT_KV_QUOTA_BYTES = 10 * 1024 * 1024;
// Per-(scope, namespace) KEY-COUNT ceiling, complementing the byte quota.
// The byte quota bounds the total *value* bytes in a bucket but not the
// number of keys, so a script could store unboundedly many tiny keys —
// bloating the store with per-row + key-string overhead the byte tally never
// sees, and making db_list return an unbounded set. A brand-new key past this
// ceiling is refused with `quota_exceeded`; overwriting an existing key is
// always allowed (a full bucket can still be updated, and an overwrite adds no
// row). Every backend defaults to this same constant, so a write that succeeds
// on one tier succeeds on all — the byte-identical-across-tiers property the
// fidelity rig depends on (db_list order is made deterministic separately, in
// makeDbEnv). It IS configurable per backend (like the byte quota) so tests can
// pin the boundary cheaply.
export const DEFAULT_KV_MAX_KEYS = 65536;
export class InMemoryMunosKv {
    quotaPerNamespace;
    maxKeysPerNamespace;
    scopes;
    // `backing` lets an embedder share the underlying map (the reference
    // host exposes its store to tests as plain Maps); omitted, the store
    // is private to this instance.
    constructor(quotaPerNamespace = DEFAULT_KV_QUOTA_BYTES, backing, maxKeysPerNamespace = DEFAULT_KV_MAX_KEYS) {
        this.quotaPerNamespace = quotaPerNamespace;
        this.maxKeysPerNamespace = maxKeysPerNamespace;
        this.scopes = backing ?? new Map();
    }
    // Synchronous backend; resolves immediately to satisfy the async port.
    get(scope, ns, key) {
        const v = this.scopes.get(scope)?.get(ns)?.get(key);
        return Promise.resolve(v ? v.slice() : undefined);
    }
    set(scope, ns, key, data) {
        const nsMap = this.scopes.get(scope) ?? new Map();
        const m = nsMap.get(ns) ?? new Map();
        // Quota is per (scope, namespace) — the projected live total in this
        // one bucket, excluding the value being overwritten.
        let projected = data.byteLength;
        for (const [k, v] of m) {
            if (k !== key)
                projected += v.byteLength;
        }
        if (projected > this.quotaPerNamespace) {
            return Promise.resolve({ ok: false, infraError: "quota_exceeded" });
        }
        // Key-count ceiling: a brand-new key past the limit is refused; overwriting
        // an existing key adds no row, so it stays allowed.
        if (!m.has(key) && m.size >= this.maxKeysPerNamespace) {
            return Promise.resolve({ ok: false, infraError: "quota_exceeded" });
        }
        m.set(key, new Uint8Array(data));
        nsMap.set(ns, m);
        this.scopes.set(scope, nsMap);
        return Promise.resolve({ ok: true });
    }
    del(scope, ns, key) {
        this.scopes.get(scope)?.get(ns)?.delete(key);
        return Promise.resolve();
    }
    list(scope, ns, prefix, start = "", limit = 0, reverse = false) {
        const m = this.scopes.get(scope)?.get(ns);
        if (!m)
            return Promise.resolve([]);
        // The dialect-free oracle: the same ordered-page definition every tier
        // funnels through in makeDbEnv (sort + cursor + reverse + limit in JS).
        return Promise.resolve(pageKeys([...m.keys()], prefix, start, limit, reverse));
    }
}
//# sourceMappingURL=kv.js.map